import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import NumericPair from '../../components/NumericPair';
import Section from '../../components/Section';
import {fetchZeroNeetSlugs} from '../utils/helpers';
import {buildDashboard2Slugs, DASHBOARD2_TOOLTIP} from '../utils/slugs';
import {ZeroNeetDashboardData} from '../utils/types';

interface Props {
    container: string;
    uoaId: string;
    survey501Id: string;
    survey502Id: string;
}

const Dashboard2 = ({container, uoaId, survey501Id, survey502Id}: Props) => {
    const slugs = buildDashboard2Slugs(Number(survey501Id), Number(survey502Id));

    const {data, isLoading, isError} = useQuery<ZeroNeetDashboardData>({
        queryKey: ['zero-neet-dashboard2', container, uoaId, survey501Id, survey502Id],
        queryFn: () => fetchZeroNeetSlugs(container, uoaId, slugs),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    // "Percorsi in corso" is the complement of ind_2_1_not_in_corso, a single backend ratio
    // covering all three "No, ..." answers to question2 (see slugs.ts) — per AP22/AP24R22, a
    // beneficiary with no update sheet at all, or with one sent only partially (so excluded by
    // the backend's completed-only filter), still counts as in corso. Clamped defensively:
    // it's only <0 if a beneficiary has a completed 502 update without a completed 501 sheet,
    // which would mean the shared-denominator assumption doesn't hold for that record.
    const clampPercent = (value: number) => Math.min(100, Math.max(0, value));
    const ind_2_1 = {
        uoa: data.ind_2_1_not_in_corso.uoa !== null ? clampPercent(Math.round((100 - data.ind_2_1_not_in_corso.uoa) * 100) / 100) : null,
        average:
            data.ind_2_1_not_in_corso.average !== null
                ? clampPercent(Math.round((100 - data.ind_2_1_not_in_corso.average) * 100) / 100)
                : null,
    };

    const displaySlugs = slugs.filter((slug) => slug.key !== 'ind_2_1_not_in_corso');

    return (
        <Section title='STATO DI AVANZAMENTO DEL PERCORSO' tooltip={DASHBOARD2_TOOLTIP}>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2}}>
                <NumericPair title='Percorsi in corso' uoaValue={ind_2_1.uoa} averageValue={ind_2_1.average} isCount={false} />
                {displaySlugs.map((slug) => (
                    <NumericPair key={slug.key} title={slug.title} uoaValue={data[slug.key].uoa} averageValue={data[slug.key].average} isCount={false} />
                ))}
            </Box>
        </Section>
    );
};

export default Dashboard2;
